import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { db, functions, storage } from '../helper/firebase.config';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';

// FIle upload to Firebase Storage with progress callback
export function uploadFileToStorage(file, filePath, onProgress) {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, filePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        onProgress && onProgress(percent);
      },
      (error) => reject(error),
      () => {
        resolve(filePath);
      }
    );
  });
}

// File download from Firebase Storage
export async function downloadFileFromStorage(filepath, filename = 'file.pptx') {
  const storageRef = ref(storage, filepath);
  const url = await getDownloadURL(storageRef);

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`HTTP error: ${res.status}`);
  }
  const blob = await res.blob();

  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);

  return true;
}

// Create new project in the firebase
export const createNewProject = async ({ fileName }) => {
  try {
    const createProject = httpsCallable(functions, 'createProject');
    const res = await createProject({ fileName });
    if (res.data && res.data.status === 'SUCCESS') {
      return res.data.projectId;
    } else {
      throw new Error('Failed to create project');
    }
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

// Fetch user's recent projects
export const fetchUserRecentProjects = async (userId, limitCount = 6) => {
  try {
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );

    let projects = [];

    try {
      // Preferred: server-side ordering (requires composite index)
      const snapshot = await getDocs(q);
      projects = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      if (error.code === 'failed-precondition') {
        // Fallback: fetch all, then sort + slice manually
        const snapshot = await getDocs(query(projectsRef, where('userId', '==', userId)));
        projects = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const normalize = (ts) =>
          ts?.toMillis?.() || (ts?.seconds ? ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6 : 0);

        projects.sort((a, b) => normalize(b.updatedAt) - normalize(a.updatedAt));
        // Apply limit AFTER sorting
        projects = projects.slice(0, limitCount);
      } else {
        throw error;
      }
    }

    return projects;
  } catch (error) {
    console.error('Fetch recent projects error:', error);
    throw error;
  }
};

// Fetch PPT JSON from URL
export const fetchPPTJson = async (jsonUrl) => {
  try {
    const response = await fetch(jsonUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch JSON');
    }
    return await response.json();
  } catch (error) {
    console.error('Fetch JSON error:', error);
    throw error;
  }
};

// Export Video
export const exportVideoAPI = async (projectId) => {
  try {
    const exportVideoFn = httpsCallable(functions, 'exportVideo');
    const res = await exportVideoFn({ projectId: projectId });
    return res.data;
  } catch (error) {
    console.error('Export video error:', error);
    throw error;
  }
};

export const setProjectLanguage = async (language, projectId) => {
  try {
    if (!language || !projectId) throw new Error('Language and Project ID are required');
    const setLanguage = httpsCallable(functions, 'setLanguage');
    const res = await setLanguage({ projectId, language });
    if (res.data?.status === 'SUCCESS') {
      return res.data;
    }
    throw new Error(res.data?.error ?? 'UNKNOWN_ERROR');
  } catch (error) {
    console.error('Set language error:', error);
    throw error;
  }
};
